import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useCart } from '../context/CartContext';
import { getActivePromo, formatEUR, promoUntilLabel } from '../utils/promo';
import WinePreviewModal from './WinePreviewModal';
import './WineCard.css';
// Lightweight placeholder image (1x1 white pixel)
var PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgZmlsbD0iI2YzZjNmMyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiNjY2MiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
var WineCard = function (_a) {
    var wine = _a.wine, language = _a.language;
    var _b = useState(false), imageError = _b[0], setImageError = _b[1];
    var _c = useState(false), imageLoaded = _c[0], setImageLoaded = _c[1];
    var _d = useState(false), isPreviewOpen = _d[0], setIsPreviewOpen = _d[1];
    var addItem = useCart().addItem;
    var promo = getActivePromo(wine);
    var handleImageError = function () {
        setImageError(true);
        setImageLoaded(true); // Consider loaded even if error
    };
    var handleImageLoad = function () {
        setImageLoaded(true);
    };
    var imageSrc = imageError ? '/api/products/default-image' : wine.imageUrl;
    var translateWineType = function (type) {
        if (language === 'en')
            return type;
        var translations = {
            'Red Wine': 'Червено вино',
            'White Wine': 'Бяло вино',
            'Rosé Wine': 'Розе вино',
            'Sparkling Wine': 'Пенливо вино',
            'Dessert Wine': 'Десертно вино',
        };
        return translations[type] || type;
    };
    var handleAddToCart = function () {
        addItem(wine);
    };
    var handleImageClick = function () {
        // Only allow on desktop (screen width > 768px)
        if (window.innerWidth > 768) {
            setIsPreviewOpen(true);
        }
    };
    // Format description: convert bullet points and preserve line breaks
    var formatDescription = function (text) {
        if (!text)
            return '';
        // Split into lines
        var lines = text.split('\n');
        var formatted = [];
        var bulletPoints = [];
        var listKey = 0;
        lines.forEach(function (line, index) {
            var trimmedLine = line.trim();
            // Check if line starts with bullet point (-, •, or *)
            if (trimmedLine.match(/^[-•*]\s+/)) {
                // Remove the bullet marker and add to list
                var content = trimmedLine.replace(/^[-•*]\s+/, '');
                bulletPoints.push(content);
            }
            else {
                // If we have accumulated bullet points, render them as a list
                if (bulletPoints.length > 0) {
                    formatted.push(_jsx("ul", { children: bulletPoints.map(function (item, i) { return (_jsx("li", { children: item }, i)); }) }, "list-".concat(listKey++)));
                    bulletPoints = [];
                }
                // Add the regular line (if not empty)
                if (trimmedLine) {
                    formatted.push(_jsx("p", { children: line }, "p-".concat(index)));
                }
                else if (formatted.length > 0) {
                    // Empty line = line break
                    formatted.push(_jsx("br", {}, "br-".concat(index)));
                }
            }
        });
        // Handle any remaining bullet points
        if (bulletPoints.length > 0) {
            formatted.push(_jsx("ul", { children: bulletPoints.map(function (item, i) { return (_jsx("li", { children: item }, i)); }) }, "list-".concat(listKey++)));
        }
        return formatted.length > 0 ? formatted : text;
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "wine-card", children: [_jsxs("div", { className: "wine-image-container", children: [!imageLoaded && (_jsx("img", { src: PLACEHOLDER_IMAGE, alt: "Loading...", className: "wine-image wine-image-placeholder" })), _jsx("img", { src: imageSrc, alt: language === 'bg' ? wine.nameBg : wine.name, className: "wine-image ".concat(imageLoaded ? 'wine-image-loaded' : 'wine-image-loading', " wine-image-clickable"), onError: handleImageError, onLoad: handleImageLoad, onClick: handleImageClick, loading: "lazy", decoding: "async" }), _jsx("div", { className: "wine-type-badge", children: translateWineType(wine.type) }), promo && _jsxs("div", { className: "wine-promo-badge", children: ["-", promo.percent, "%"] })] }), _jsxs("div", { className: "wine-content", children: [_jsx("h3", { className: "wine-name", children: language === 'bg' ? wine.nameBg : wine.name }), _jsxs("div", { className: "wine-details", children: [_jsx("span", { className: "wine-region", children: wine.region }), wine.year && _jsx("span", { className: "wine-vintage", children: wine.year })] }), _jsx("div", { className: "wine-description", children: formatDescription(language === 'bg' ? wine.descriptionBg : wine.description) }), _jsxs("div", { className: "wine-specs", children: [_jsxs("div", { className: "wine-specs-left", children: [_jsx("span", { className: "wine-alcohol", children: wine.alcoholContent }), wine.servingTemperature && (_jsxs("span", { className: "wine-temperature", children: ["\uD83C\uDF21\uFE0F ", wine.servingTemperature] }))] }), _jsx("div", { className: "wine-price-container", children: promo ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "wine-price-old", children: formatEUR(wine.priceEUR) }), _jsx("span", { className: "wine-price wine-price-promo", children: formatEUR(promo.price) }), _jsxs("span", { className: "wine-promo-until", children: ["-", promo.percent, "% ", promoUntilLabel(promo, language)] })] })) : (_jsx("span", { className: "wine-price", children: formatEUR(wine.priceEUR) })) })] }), _jsxs("button", { className: "add-to-cart-btn", onClick: handleAddToCart, children: [_jsx("svg", { className: "cart-icon", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M9 2L7.17 4H2V6H3.5L5.5 16.5C5.5 17.88 6.62 19 8 19H18C19.38 19 20.5 17.88 20.5 16.5L22.5 6H24V4H18.83L17 2H9ZM9 4H15L16.17 6H7.83L9 4ZM5.7 8H20.3L18.5 16.5C18.5 16.78 18.28 17 18 17H8C7.72 17 7.5 16.78 7.5 16.5L5.7 8ZM11 10V15H13V10H11Z", fill: "currentColor" }) }), language === 'bg' ? 'Добави в количката' : 'Add to cart'] })] })] }), _jsx(WinePreviewModal, { wine: wine, isOpen: isPreviewOpen, onClose: function () { return setIsPreviewOpen(false); }, language: language })] }));
};
export default WineCard;
